import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { z } from 'zod';

export interface ApiError {
  status: number;
  code:   string;
  message: string;
  fields?: Record<string, string>;
  requestId?: string;
}

const baseURL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,            // send/receive arp_session cookie
  headers: { 'Content-Type': 'application/json' },
});

// 401 handler — if a global handler is registered, it can wipe auth + nav.
type On401 = () => void;
let on401: On401 | null = null;
export function setOn401Handler(fn: On401 | null) { on401 = fn; }

apiClient.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    if (error.response?.status === 401 && on401) {
      on401();
    }
    return Promise.reject(toApiError(error));
  },
);

function toApiError(err: AxiosError): ApiError {
  const status = err.response?.status ?? 0;
  const data = err.response?.data as
    | { error?: { code?: string; message?: string; fields?: Record<string, string> }; request_id?: string; detail?: string }
    | undefined;

  return {
    status,
    code:    data?.error?.code ?? (status === 401 ? 'unauthorized' : 'unknown'),
    message: data?.error?.message ?? data?.detail ?? err.message,
    fields:  data?.error?.fields,
    requestId: data?.request_id,
  };
}

/**
 * Helper that runs a request, then validates the body with a Zod schema.
 * Throws ApiError on HTTP failure or schema mismatch.
 */
export async function get<T extends z.ZodTypeAny>(
  url: string,
  schema: T,
  config?: AxiosRequestConfig,
): Promise<z.infer<T>> {
  const resp = await apiClient.get(url, config);
  const parsed = schema.safeParse(resp.data);
  if (!parsed.success) {
    throw {
      status: 0, code: 'schema_mismatch',
      message: parsed.error.message,
    } as ApiError;
  }
  return parsed.data;
}

export async function post<TIn, TOut extends z.ZodTypeAny>(
  url: string,
  body: TIn,
  schema: TOut,
  config?: AxiosRequestConfig,
): Promise<z.infer<TOut>> {
  const resp = await apiClient.post(url, body, config);
  const parsed = schema.safeParse(resp.data);
  if (!parsed.success) {
    throw {
      status: 0, code: 'schema_mismatch',
      message: parsed.error.message,
    } as ApiError;
  }
  return parsed.data;
}

export async function postNoBody(url: string, body?: unknown): Promise<void> {
  await apiClient.post(url, body ?? {});
}

export async function patch<TIn, TOut extends z.ZodTypeAny>(
  url: string,
  body: TIn,
  schema: TOut,
  config?: AxiosRequestConfig,
): Promise<z.infer<TOut>> {
  const resp = await apiClient.patch(url, body, config);
  const parsed = schema.safeParse(resp.data);
  if (!parsed.success) {
    throw {
      status: 0, code: 'schema_mismatch',
      message: parsed.error.message,
    } as ApiError;
  }
  return parsed.data;
}

export async function patchNoBody(url: string, body: unknown): Promise<void> {
  await apiClient.patch(url, body);
}

export async function put<TIn, TOut extends z.ZodTypeAny>(
  url: string,
  body: TIn,
  schema: TOut,
  config?: AxiosRequestConfig,
): Promise<z.infer<TOut>> {
  const resp = await apiClient.put(url, body, config);
  const parsed = schema.safeParse(resp.data);
  if (!parsed.success) {
    throw {
      status: 0, code: 'schema_mismatch',
      message: parsed.error.message,
    } as ApiError;
  }
  return parsed.data;
}

export async function del(url: string): Promise<void> {
  await apiClient.delete(url);
}
